import { useEffect, useState } from 'react';
import { Entry } from '../types/pool';
import { api } from '../services/api';
import { CountryFlag } from './CountryFlag';

interface LineupModalProps {
  entry: Entry;
  poolId: string;
  onClose: () => void;
}

interface LineupPlayer {
  position: string;
  player: {
    id: string;
    name: string;
    position: string;
    team: {
      id: string;
      name: string;
      abbreviation: string;
    };
  } | null;
  score: number;
  events: { eventId: string; eventName: string; isFinal: boolean; basePoints: number; multiplier: number; points: number }[];
}

// Position order for display (covers multiple pool types)
const POSITION_ORDER = [
  'QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE',
  'FLEX1', 'FLEX2', 'FLEX3', 'FLEX4', 'K', 'DEF',
  'F1', 'F2', 'F3', 'F4', 'F5',
  'D1', 'D2', 'D3', 'D4', 'G',
];

function sortPositions(positions: [string, any][]): [string, any][] {
  return positions.sort((a, b) => {
    const indexA = POSITION_ORDER.indexOf(a[0]);
    const indexB = POSITION_ORDER.indexOf(b[0]);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });
}

// Region colors
const REGION_COLORS: Record<string, string> = {
  East: 'bg-blue-100 text-blue-800',
  West: 'bg-red-100 text-red-800',
  South: 'bg-green-100 text-green-800',
  Midwest: 'bg-purple-100 text-purple-800',
};

/**
 * Render Pick6 (BRACKET_PREDICTION) picks with alive/eliminated indicators
 */
function Pick6PicksView({ picks }: { picks: any }) {
  const [eliminatedIds, setEliminatedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.getEliminatedNcaaTeams()
      .then(data => setEliminatedIds(new Set(data.eliminatedTeamIds || [])))
      .catch(() => {});
  }, []);

  const teams = picks?.teams || [];
  if (teams.length === 0) {
    return <p className="text-center py-8 text-gray-500">No picks available</p>;
  }

  const aliveCount = teams.filter((t: any) => !eliminatedIds.has(t.teamId)).length;

  return (
    <div className="space-y-2">
      {eliminatedIds.size > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 px-1 pb-1">
          <span><span className="font-semibold text-green-600">{aliveCount}</span> alive / <span className="font-semibold text-red-600">{teams.length - aliveCount}</span> eliminated</span>
        </div>
      )}
      {teams.map((team: any, idx: number) => {
        const isEliminated = eliminatedIds.has(team.teamId);
        return (
          <div key={team.teamId || idx} className={`flex items-center justify-between border rounded-lg p-3 ${isEliminated ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center gap-3">
              <span className={`text-lg font-bold w-8 ${isEliminated ? 'text-red-400' : 'text-green-600'}`}>#{idx + 1}</span>
              <div>
                <p className={`font-semibold ${isEliminated ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  ({team.seed}) {team.teamName}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {team.region && (
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${REGION_COLORS[team.region] || 'bg-gray-100 text-gray-700'}`}>
                      {team.region}
                    </span>
                  )}
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${isEliminated ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {isEliminated ? 'Eliminated' : 'Alive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Render Knockout (DAILY_PICKS) picks
 */
function KnockoutPicksView({ picks }: { picks: any }) {
  const dailyPicks = picks?.picks || {};
  const dates = Object.keys(dailyPicks).sort();
  if (dates.length === 0) {
    return <p className="text-center py-8 text-gray-500">No picks available</p>;
  }
  return (
    <div className="space-y-4">
      {picks.status && (
        <div className={`text-center py-2 px-4 rounded-lg text-sm font-medium ${
          picks.status === 'ALIVE' ? 'bg-green-100 text-green-800' :
          picks.status === 'BOUGHT_BACK' ? 'bg-orange-100 text-orange-800' :
          'bg-red-100 text-red-800'
        }`}>
          Status: {picks.status}{picks.buyBackCount ? ` (${picks.buyBackCount}/3 buy-backs used)` : ''}
        </div>
      )}
      {dates.map(date => {
        const dayPicks = dailyPicks[date] || [];
        return (
          <div key={date} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <p className="font-semibold text-gray-700 text-sm">
                {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </p>
            </div>
            <div className="p-3 space-y-2">
              {dayPicks.map((pick: any, idx: number) => (
                <div key={pick.teamId || idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-500">({pick.seed || '?'})</span>
                    <span className="font-medium text-gray-900">{pick.teamName}</span>
                    {pick.region && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${REGION_COLORS[pick.region] || 'bg-gray-100 text-gray-700'}`}>
                        {pick.region}
                      </span>
                    )}
                  </div>
                  {pick.result && pick.result !== 'PENDING' && (
                    <span className={`text-sm font-semibold ${pick.result === 'WIN' ? 'text-green-600' : 'text-red-600'}`}>
                      {pick.result === 'WIN' ? '✓ Win' : '✗ Loss'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Render NFL No-Repeat (WEEKLY_LINEUP) picks — one card per visible week.
 * The server replaces weeks whose first game hasn't kicked off with
 * `{ hidden: true }` (other users' entries), so hidden weeks show a note
 * instead of a lineup.
 */
function WeeklyLineupPicksView({ picks }: { picks: any }) {
  const slotOrder = ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'FLEX'];
  const allWeeks = Object.entries((picks?.weeks || {}) as Record<string, any>).sort(
    ([a], [b]) => Number(a) - Number(b),
  );
  const visible = allWeeks.filter(
    ([, w]) => !w?.hidden && w?.positions && Object.keys(w.positions).length > 0,
  );
  const hiddenCount = allWeeks.filter(([, w]) => w?.hidden).length;

  if (visible.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-700 font-medium">No lineups visible yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Each week's lineups stay hidden until that week's first kickoff.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {visible.map(([weekNum, w]) => (
        <div key={weekNum} className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900">Week {weekNum}</h4>
            <span className="text-sm font-semibold text-emerald-700">
              {Number(w.weekScore || 0).toFixed(1)} pts
            </span>
          </div>
          <div className="space-y-1">
            {slotOrder.map((slot) => {
              const p = w.positions[slot];
              return (
                <div
                  key={slot}
                  className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0"
                >
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded w-14 text-center">
                    {slot}
                  </span>
                  <span className={`flex-1 text-right text-sm ${p ? 'text-gray-900' : 'text-gray-400'}`}>
                    {p ? p.playerName : '— empty —'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {hiddenCount > 0 && (
        <p className="text-xs text-gray-400 text-center">
          {hiddenCount} upcoming {hiddenCount === 1 ? 'week is' : 'weeks are'} hidden until kickoff.
        </p>
      )}
    </div>
  );
}

/**
 * Render World Cup Pick 6 (WORLD_CUP_BRACKET_PREDICTION) picks.
 *
 * Shows each of the 6 nation picks with flag, FIFA seed tier, and the highest
 * round the team has reached so far. `picks.scoredRounds[teamId]` (an array of
 * round keys like ['GROUP', 'R32']) is set by the scoring engine; falls back
 * gracefully if not yet populated.
 */
function WorldCupPick6PicksView({ picks }: { picks: any }) {
  const teams: any[] = picks?.teams || [];
  if (teams.length === 0) {
    return <p className="text-center py-8 text-gray-500">No picks available</p>;
  }

  // Round → multiplier (mirrors WORLD_CUP_PICK6_ROUND_MULTIPLIERS)
  const ROUND_FACTOR: Record<string, number> = { GROUP: 4, R32: 5, R16: 7, QF: 9, SF: 11, FINAL: 13 };
  const ROUND_LABEL: Record<string, string> = {
    GROUP: 'Group Stage',
    R32: 'Round of 32',
    R16: 'Round of 16',
    QF: 'Quarterfinals',
    SF: 'Semifinals',
    FINAL: 'Final',
  };
  const ORDER = ['GROUP', 'R32', 'R16', 'QF', 'SF', 'FINAL'];

  const scoredRounds: Record<string, string[]> = picks?.scoredRounds || {};

  const aliveCount = teams.filter((t: any) => {
    // alive = not in eliminatedTeams; pre-tournament defaults to alive
    return picks?.eliminatedTeams ? !picks.eliminatedTeams.includes(t.teamId) : true;
  }).length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm text-gray-600 px-1 pb-1">
        <span>
          <span className="font-semibold text-emerald-700">{aliveCount}</span> alive ·{' '}
          <span className="font-semibold text-rose-600">{teams.length - aliveCount}</span> out
        </span>
      </div>
      {teams.map((team: any, idx: number) => {
        const rounds: string[] = scoredRounds[team.teamId] || [];
        const reached = rounds.length > 0 ? rounds[rounds.length - 1] : null;
        const isOut = picks?.eliminatedTeams?.includes(team.teamId);
        const earned = rounds.reduce((sum, r) => sum + (team.seed * (ROUND_FACTOR[r] || 0)), 0);

        return (
          <div
            key={team.teamId || idx}
            className={`flex items-center justify-between border rounded-lg p-3 ${
              isOut ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className={`text-lg font-bold w-8 ${isOut ? 'text-rose-400' : 'text-emerald-700'}`}>
                #{idx + 1}
              </span>
              {team.isoCode && <CountryFlag iso={team.isoCode} alt={team.teamName} size="md" rounded />}
              <div className="min-w-0">
                <p className={`font-semibold truncate ${isOut ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  Seed {team.seed} · {team.teamName}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {reached ? (
                    <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                      Reached: {ROUND_LABEL[reached] ?? reached}
                    </span>
                  ) : (
                    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                      Pre-tournament
                    </span>
                  )}
                  <span
                    className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${
                      isOut ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                    }`}
                  >
                    {isOut ? 'Eliminated' : 'Alive'}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right pl-2">
              <p className="text-xs text-gray-500">Earned</p>
              <p className={`text-lg font-bold ${isOut ? 'text-gray-400' : 'text-emerald-700'}`}>{earned}</p>
            </div>
          </div>
        );
      })}
      {/* Reference: round multipliers */}
      <p className="text-xs text-gray-500 pt-2 border-t">
        Scoring: Seed × {ORDER.map((r) => `${ROUND_LABEL[r]} ×${ROUND_FACTOR[r]}`).join(' · ')}
      </p>
    </div>
  );
}

/**
 * Render World Cup Survivor (WORLD_CUP_SURVIVOR) picks.
 * Two sections: Phase 1 group-stage picks (with advanced/out indicators) and
 * Phase 2 per-round knockout picks (with WIN/LOSS results).
 */
function WorldCupSurvivorPicksView({ picks }: { picks: any }) {
  const ROUND_LABEL: Record<string, string> = {
    R32: 'Round of 32',
    R16: 'Round of 16',
    QF: 'Quarterfinals',
    SF: 'Semifinals',
    FINAL: 'Final',
  };
  const ORDER = ['R32', 'R16', 'QF', 'SF', 'FINAL'];

  const groupTeams: any[] = picks?.groupStageTeams || [];
  const knockoutPicks: Record<string, any> = picks?.knockoutPicks || {};
  const status = picks?.status || 'ALIVE';
  const eliminatedRound = picks?.eliminatedRound;

  return (
    <div className="space-y-4">
      {/* Status banner */}
      <div
        className={`text-center py-2 px-4 rounded-lg text-sm font-medium ${
          status === 'ALIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
        }`}
      >
        Status: {status}
        {eliminatedRound && status === 'ELIMINATED' && (
          <> · eliminated in {eliminatedRound === 'GROUP' ? 'Group Stage' : ROUND_LABEL[eliminatedRound] || eliminatedRound}</>
        )}
      </div>

      {/* Phase 1 — Group stage picks */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <p className="font-semibold text-gray-700 text-sm">Phase 1 — Group Stage (need all 6 to advance)</p>
          <span className="text-xs text-gray-500">{groupTeams.length} picks</span>
        </div>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {groupTeams.length === 0 ? (
            <p className="text-sm text-gray-500 col-span-2 text-center py-3">No Phase 1 picks submitted</p>
          ) : (
            groupTeams.map((t: any, idx: number) => {
              const advanced = t.advanced ?? 'PENDING';
              const tone =
                advanced === 'YES'
                  ? 'bg-emerald-50 border-emerald-200'
                  : advanced === 'NO'
                  ? 'bg-rose-50 border-rose-200'
                  : 'bg-gray-50 border-gray-200';
              return (
                <div key={t.teamId || idx} className={`flex items-center justify-between border rounded-lg p-2 ${tone}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {t.isoCode && <CountryFlag iso={t.isoCode} alt={t.teamName} size="sm" rounded />}
                    <span className={`font-medium truncate ${advanced === 'NO' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      Seed {t.seed} · {t.teamName}
                    </span>
                  </div>
                  {advanced === 'YES' && <span className="text-xs font-semibold text-emerald-700">✓ advanced</span>}
                  {advanced === 'NO' && <span className="text-xs font-semibold text-rose-600">✗ out</span>}
                  {advanced === 'PENDING' && <span className="text-xs text-gray-500">pending</span>}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Phase 2 — Knockout picks */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <p className="font-semibold text-gray-700 text-sm">Phase 2 — Knockout (one team per round)</p>
        </div>
        <div className="p-3 space-y-2">
          {ORDER.map((round) => {
            const pick = knockoutPicks[round];
            if (!pick) {
              return (
                <div key={round} className="flex items-center justify-between text-sm text-gray-500 italic px-2">
                  <span>{ROUND_LABEL[round]}</span>
                  <span>— no pick yet —</span>
                </div>
              );
            }
            const result = pick.result || 'PENDING';
            return (
              <div key={round} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs uppercase tracking-wide text-gray-500 w-24">{ROUND_LABEL[round]}</span>
                  {pick.isoCode && <CountryFlag iso={pick.isoCode} alt={pick.teamName} size="sm" rounded />}
                  <span className={`font-medium truncate ${result === 'LOSS' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    Seed {pick.seed} · {pick.teamName}
                  </span>
                </div>
                {result === 'WIN' && <span className="text-sm font-semibold text-emerald-600">✓ Win</span>}
                {result === 'LOSS' && <span className="text-sm font-semibold text-rose-600">✗ Loss</span>}
                {result === 'PENDING' && <span className="text-xs text-gray-500">pending</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Render F1 (F1_SEQUENTIAL_RACES) picks
 */
function F1PicksView({ picks }: { picks: any }) {
  const racePicks = picks?.picks || [];
  if (racePicks.length === 0) {
    return <p className="text-center py-8 text-gray-500">No picks available</p>;
  }
  return (
    <div className="space-y-2">
      {racePicks.map((race: any, idx: number) => (
        <div key={race.raceId || idx} className="bg-white border border-gray-200 rounded-lg p-3">
          <p className="font-semibold text-gray-700 text-sm mb-1">Race {race.raceNumber || idx + 1}</p>
          <div className="flex gap-3">
            {(race.drivers || []).map((d: any, dIdx: number) => (
              <span key={dIdx} className="text-sm text-gray-900">
                {d.driverName || d.playerId}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function LineupModal({ entry, poolId, onClose }: LineupModalProps) {
  const [lineup, setLineup] = useState<LineupPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine pick type from entry picks
  const picks = entry.picks as any;
  const pickType = picks?.type;
  const isRosterBased = pickType === 'ROSTER_SELECTION';

  useEffect(() => {
    // Only fetch lineup API for roster-based pools (it returns player/position data)
    // For bracket/knockout/F1, we already have all the data in entry.picks
    if (isRosterBased) {
      loadLineupDetails();
    } else {
      setLoading(false);
    }
  }, [entry.id]);

  const loadLineupDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getEntryLineup(poolId, entry.id);
      setLineup(data.players || []);
    } catch (err) {
      console.error('Failed to load lineup details:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load lineup';
      if (errorMessage.includes('picks') || errorMessage.includes('deadline') || errorMessage.includes('Forbidden')) {
        setError('Picks are hidden until after the pool deadline.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Build summary stats based on pick type
  const getSummaryStats = () => {
    if (pickType === 'BRACKET_PREDICTION') {
      return { label1: 'Total Score', val1: Number(entry.score).toFixed(2), label2: 'Teams', val2: String(picks?.teams?.length || 0), label3: 'Seed Total', val3: String((picks?.teams || []).reduce((s: number, t: any) => s + (t.seed || 0), 0)) };
    }
    if (pickType === 'DAILY_PICKS') {
      const dates = Object.keys(picks?.picks || {});
      const totalPicks = dates.reduce((sum: number, d: string) => sum + ((picks?.picks?.[d] || []).length), 0);
      return { label1: 'Status', val1: picks?.status || 'ALIVE', label2: 'Days Played', val2: String(dates.length), label3: 'Total Picks', val3: String(totalPicks) };
    }
    if (pickType === 'F1_SEQUENTIAL_RACES') {
      return { label1: 'Total Score', val1: Number(entry.score).toFixed(2), label2: 'Races', val2: String(picks?.picks?.length || 0), label3: 'Phase', val3: picks?.phase || 'REGULAR' };
    }
    if (pickType === 'WORLD_CUP_BRACKET_PREDICTION') {
      const eliminatedCount = (picks?.eliminatedTeams || []).length;
      const aliveCount = (picks?.teams?.length || 0) - eliminatedCount;
      return {
        label1: 'Total Score',
        val1: Number(entry.score).toFixed(2),
        label2: 'Teams Alive',
        val2: `${aliveCount}/${picks?.teams?.length || 0}`,
        label3: 'Avg Seed',
        val3: picks?.teams?.length
          ? ((picks.teams.reduce((s: number, t: any) => s + (t.seed || 0), 0)) / picks.teams.length).toFixed(1)
          : '0',
      };
    }
    if (pickType === 'WORLD_CUP_SURVIVOR') {
      const knockoutCount = Object.keys(picks?.knockoutPicks || {}).length;
      return {
        label1: 'Status',
        val1: picks?.status || 'ALIVE',
        label2: 'Group Picks',
        val2: String((picks?.groupStageTeams || []).length),
        label3: 'Knockout Picks',
        val3: `${knockoutCount}/5`,
      };
    }
    if (pickType === 'WEEKLY_LINEUP') {
      const weekEntries = Object.values((picks?.weeks || {}) as Record<string, any>);
      const setWeeks = weekEntries.filter(
        (w) => w?.hidden || (w?.positions && Object.keys(w.positions).length > 0),
      ).length;
      const playersUsed = new Set(
        weekEntries.flatMap((w) =>
          w?.positions ? Object.values(w.positions).map((p: any) => p?.playerId).filter(Boolean) : [],
        ),
      ).size;
      return {
        label1: 'Season Score',
        val1: Number(entry.score).toFixed(1),
        label2: 'Weeks Set',
        val2: String(setWeeks),
        label3: 'Players Used',
        val3: String(playersUsed),
      };
    }
    // Roster-based default
    return { label1: 'Total Score', val1: Number(entry.score).toFixed(2), label2: 'Players', val2: loading ? '...' : String(lineup.length), label3: 'Avg Per Player', val3: loading ? '...' : lineup.length > 0 ? (Number(entry.score) / lineup.length).toFixed(2) : '0.00' };
  };

  const stats = getSummaryStats();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{entry.entryName || 'Entry Details'}</h2>
            <p className="text-blue-100 text-sm mt-1">
              {(entry as any).displayName || (entry as any).username || 'Anonymous'}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Score Summary */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">{stats.label1}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.val1}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">{stats.label2}</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.val2}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">{stats.label3}</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.val3}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <svg className="w-16 h-16 text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Picks Hidden</h3>
              <p className="text-gray-600 text-center max-w-sm">{error}</p>
            </div>
          ) : (
            <div className="px-6 py-4">
              {/* Pick6 (Bracket Prediction) */}
              {pickType === 'BRACKET_PREDICTION' && <Pick6PicksView picks={picks} />}

              {/* Knockout (Daily Picks) */}
              {pickType === 'DAILY_PICKS' && <KnockoutPicksView picks={picks} />}

              {/* F1 */}
              {pickType === 'F1_SEQUENTIAL_RACES' && <F1PicksView picks={picks} />}

              {/* World Cup Pick 6 */}
              {pickType === 'WORLD_CUP_BRACKET_PREDICTION' && <WorldCupPick6PicksView picks={picks} />}

              {/* World Cup Survivor */}
              {pickType === 'WORLD_CUP_SURVIVOR' && <WorldCupSurvivorPicksView picks={picks} />}

              {/* NFL No-Repeat (Weekly Lineup) */}
              {pickType === 'WEEKLY_LINEUP' && <WeeklyLineupPicksView picks={picks} />}

              {/* Picks stripped server-side (e.g. pool still open) */}
              {!pickType && !isRosterBased && (
                <div className="flex flex-col items-center justify-center py-12 px-6">
                  <svg className="w-16 h-16 text-amber-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Picks Hidden</h3>
                  <p className="text-gray-600 text-center max-w-sm">
                    Picks are hidden until they're revealed for everyone.
                  </p>
                </div>
              )}

              {/* Roster-based pools */}
              {isRosterBased && (
                lineup.length > 0 ? (
                  <div className="space-y-3">
                    {sortPositions(lineup.map(p => [p.position, p] as [string, LineupPlayer])).map(([position, lineupPlayer]) => (
                      <div key={position} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded">
                                {position}
                              </span>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {lineupPlayer.player?.name || 'Unknown Player'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {lineupPlayer.player?.team?.name || 'Team'} • {lineupPlayer.player?.position || position}
                                </p>
                              </div>
                            </div>
                            {lineupPlayer.events && lineupPlayer.events.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <p className="text-xs font-semibold text-gray-700 mb-2">Game Scores:</p>
                                <div className="flex flex-wrap gap-2">
                                  {lineupPlayer.events.map((event: LineupPlayer['events'][0], idx: number) => (
                                    <div key={idx} className="bg-gray-50 px-2 py-1 rounded text-xs">
                                      <span className="text-gray-600">{event.eventName}:</span>
                                      <span className="ml-1 font-semibold text-gray-900">
                                        {event.points.toFixed(2)}
                                        {event.isFinal && <span className="text-green-600 ml-1">(2x)</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-2xl font-bold text-gray-900">
                              {lineupPlayer.score?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-xs text-gray-500">points</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <p>No lineup data available</p>
                  </div>
                )
              )}

              {/* Unknown pick type fallback */}
              {!pickType && (
                <div className="text-center py-12 text-gray-500">
                  <p>No picks data available</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
